import React from 'react';
import Icon from '../../../components/AppIcon';

const SpecificationCard = ({ specifications, language, farmData }) => {
  const specItems = [
    {
      icon: 'Droplets',
      label: { en: 'Moisture Content', zh: '水分含量' },
      value: `${specifications?.moisture_pct}%`
    },
    {
      icon: 'Factory',
      label: { en: 'Processing Method', zh: '加工方法' },
      value: specifications?.processing_type
    },
    {
      icon: 'Award',
      label: { en: 'Grade', zh: '等级' },
      value: specifications?.grade
    },
    {
      icon: 'Mountain',
      label: { en: 'Elevation', zh: '海拔' },
      value: `${specifications?.altitude_m}m`
    },
    {
      icon: 'Calendar',
      label: { en: 'Production Year', zh: '生产年份' },
      value: specifications?.production_year
    },
    {
      icon: 'Package',
      label: { en: 'Available Quantity', zh: '可用数量' },
      value: `${specifications?.available_qty} kg`
    }
  ];

  // Calculate farm area from GeoJSON if available
  const calculateFarmArea = () => {
    if (!farmData?.geojson) return null;
    
    try {
      const geojson = typeof farmData?.geojson === 'string' ? JSON.parse(farmData?.geojson) : farmData?.geojson;
      let coordinates = [];
      
      if (geojson?.type === 'Polygon' && geojson?.coordinates?.[0]) {
        coordinates = geojson?.coordinates?.[0];
      } else if (geojson?.type === 'Feature' && geojson?.geometry?.type === 'Polygon') {
        coordinates = geojson?.geometry?.coordinates?.[0];
      }
      
      if (coordinates?.length < 3) return null;
      
      // Simple area calculation (approximate)
      let area = 0;
      for (let i = 0; i < coordinates?.length - 1; i++) {
        const [x1, y1] = coordinates?.[i];
        const [x2, y2] = coordinates?.[i + 1];
        area += (x1 * y2) - (x2 * y1);
      }
      return Math.abs(area / 2) * 111 * 111; // Convert to approximate hectares
    } catch (error) {
      console.error('Failed to calculate farm area:', error);
      return null;
    }
  };

  const farmArea = calculateFarmArea();

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-md hover:shadow-lg transition-smooth animate-slide-up">
      <h2 className="text-xl font-heading font-semibold mb-6 coffee-text-dark">
        {language === 'en' ? 'Specifications' : '规格'}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {specItems?.map((item, index) => (
          <div key={index} className="flex items-start gap-3 p-3 rounded-lg hover:bg-secondary/5 transition-smooth">
            <div className="w-10 h-10 coffee-bg-medium rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
              <Icon name={item?.icon} size={20} color="#FFFFFF" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">{item?.label?.[language]}</p>
              <p className="font-medium">{item?.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Farm Area Information */}
      {farmData && (
        <div className="mt-6 pt-6 border-t border-border">
          <p className="text-sm coffee-text-medium mb-3 font-medium flex items-center gap-2">
            <Icon name="Map" size={16} />
            {language === 'en' ? 'Farm Information' : '农场信息'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {farmData?.farmSizeHa && (
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Maximize2" size={14} className="coffee-text-medium" />
                <span className="text-muted-foreground">{language === 'en' ? 'Farm Size:' : '农场面积：'}</span>
                <span className="font-medium">{farmData?.farmSizeHa} ha</span>
              </div>
            )}
            {farmArea && (
              <div className="flex items-center gap-2 text-sm">
                <Icon name="MapPin" size={14} className="coffee-text-medium" />
                <span className="text-muted-foreground">{language === 'en' ? 'Mapped Area:' : '测绘面积：'}</span>
                <span className="font-medium">{farmArea?.toFixed(2)} ha</span>
              </div>
            )}
            {farmData?.altitudeM && (
              <div className="flex items-center gap-2 text-sm">
                <Icon name="Mountain" size={14} className="coffee-text-medium" />
                <span className="text-muted-foreground">{language === 'en' ? 'Altitude:' : '海拔：'}</span>
                <span className="font-medium">{farmData?.altitudeM}m</span>
              </div>
            )}
            {farmData?.region && (
              <div className="flex items-center gap-2 text-sm">
                <Icon name="MapPin" size={14} className="coffee-text-medium" />
                <span className="text-muted-foreground">{language === 'en' ? 'Region:' : '地区：'}</span>
                <span className="font-medium">{farmData?.region}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {specifications?.certifications && specifications?.certifications?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <p className="text-sm coffee-text-medium mb-3 font-medium">
            {language === 'en' ? 'Certifications' : '认证'}
          </p>
          <div className="flex flex-wrap gap-2">
            {specifications?.certifications?.map((cert, index) => (
              <span
                key={index}
                className="px-3 py-1 coffee-bg-dark rounded-full text-sm font-medium shadow-sm hover:shadow-md transition-smooth animate-scale-in"
              >
                {cert}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecificationCard;